import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { RayResult, SimulationSnapshot, ViewMode, VisibilityState } from '../simulation/types';

export interface SceneLayerVisibility {
  cube: boolean;
  meshes: boolean;
  hemisphere: boolean;
  emitters: boolean;
  desiredRays: boolean;
  successfulRays: boolean;
  projectionRays: boolean;
  projectionEmitters: boolean;
  blockedRays: boolean;
  successfulIntersections: boolean;
  projectionIntersections: boolean;
  blockedIntersections: boolean;
}

export function resolveLayerVisibility(
  mode: ViewMode,
  visibility: VisibilityState,
): SceneLayerVisibility {
  const structureMode = mode === 'STRUCTURE';
  const laserMode = mode === 'LASERS';
  const occlusionMode = mode === 'OCCLUSION';
  const targetMode = mode === 'TARGET';
  const voxelMode = mode === 'VOXELS';
  const futureMode = mode === 'HEATMAP' || mode === 'FINAL_IMAGE';

  return {
    cube: !targetMode,
    meshes: targetMode ? visibility.projectionMeshes : visibility.meshes,
    hemisphere: targetMode
      ? visibility.projectionRays
      : !voxelMode && visibility.lasers,
    emitters: !targetMode && !voxelMode && visibility.lasers,
    desiredRays:
      !structureMode && !targetMode && !voxelMode && visibility.lasers &&
      (visibility.desiredRays || occlusionMode) && !futureMode,
    successfulRays:
      !structureMode && !targetMode && !voxelMode && !futureMode && visibility.lasers,
    projectionRays: targetMode && visibility.projectionRays,
    projectionEmitters: targetMode && visibility.projectionRays,
    blockedRays:
      !structureMode && !targetMode && !voxelMode && visibility.lasers &&
      visibility.blockedRays && (laserMode || occlusionMode),
    successfulIntersections:
      voxelMode || (!structureMode && !targetMode && !futureMode && visibility.intersections),
    projectionIntersections: targetMode && visibility.intersections,
    blockedIntersections:
      !structureMode && !targetMode && !voxelMode && !futureMode && visibility.intersections &&
      visibility.blockedRays,
  };
}

const COLORS = {
  cube: 0x92b8ad,
  mesh: new THREE.Color('#74b9aa'),
  emitter: new THREE.Color('#e8f6d5'),
  desired: new THREE.Color('#8097a0'),
  success: new THREE.Color('#89e8b4'),
  blocked: new THREE.Color('#ff9c62'),
  impact: new THREE.Color('#d9ffd9'),
  background: 0x06100f,
};

export interface ProjectionPointSizes {
  halo: number;
  core: number;
}

export function projectionPointSizes(
  meanSeparation: number | null,
  cameraDistance = 0.52,
): ProjectionPointSizes {
  const separation = meanSeparation ?? 0.03;
  const zoomCompensation = THREE.MathUtils.clamp(cameraDistance / 0.52, 0.35, 4);
  return {
    halo: THREE.MathUtils.clamp(separation * 0.62, 0.004, 0.014) * zoomCompensation,
    core: THREE.MathUtils.clamp(separation * 0.28, 0.0018, 0.006) * zoomCompensation,
  };
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.Line || child instanceof THREE.Points) {
      child.geometry.dispose();
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      for (const material of materials) material.dispose();
    }
  });
}

function clearGroup(group: THREE.Group): void {
  for (const child of [...group.children]) {
    group.remove(child);
    disposeObject(child);
  }
}

function glowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  if (context) {
    const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.18, 'rgba(230,255,226,1)');
    gradient.addColorStop(0.5, 'rgba(137,232,180,.38)');
    gradient.addColorStop(1, 'rgba(137,232,180,0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 64, 64);
  }
  return new THREE.CanvasTexture(canvas);
}

export class SceneRenderer {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.001, 20);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly isWebGl2: boolean;
  private readonly controls: OrbitControls;
  private readonly root = new THREE.Group();
  private readonly cubeGroup = new THREE.Group();
  private readonly meshGroup = new THREE.Group();
  private readonly hemisphereGroup = new THREE.Group();
  private readonly emitterGroup = new THREE.Group();
  private readonly desiredRayGroup = new THREE.Group();
  private readonly successfulRayGroup = new THREE.Group();
  private readonly projectionRayGroup = new THREE.Group();
  private readonly projectionEmitterGroup = new THREE.Group();
  private readonly blockedRayGroup = new THREE.Group();
  private readonly successfulIntersectionGroup = new THREE.Group();
  private readonly projectionIntersectionGroup = new THREE.Group();
  private readonly blockedIntersectionGroup = new THREE.Group();
  private readonly pointTexture = glowTexture();
  private lastFrameAt = performance.now();
  private frameSamples: number[] = [];
  private visibility: VisibilityState;
  private mode: ViewMode = 'TARGET';
  private apparatusCameraDistance = 1.45;
  private apparatusTargetY = -0.28;
  private projectionMeanSeparation: number | null = null;

  constructor(
    private readonly host: HTMLElement,
    initialVisibility: VisibilityState,
    private readonly onFps: (fps: number) => void,
  ) {
    this.visibility = { ...initialVisibility };
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2', {
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this.isWebGl2 = context !== null;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      context: context ?? undefined,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.domElement.setAttribute('aria-label', 'Escena 3D interactiva del volumen óptico');
    this.host.append(this.renderer.domElement);

    this.camera.position.set(0, 0.03, 0.52);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.055;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 0.18;
    this.controls.maxDistance = 4;
    this.controls.target.set(0, 0, 0);

    this.scene.background = new THREE.Color(COLORS.background);
    this.scene.fog = new THREE.FogExp2(COLORS.background, 0.82);
    this.scene.add(this.root);
    this.root.add(
      this.cubeGroup,
      this.meshGroup,
      this.hemisphereGroup,
      this.emitterGroup,
      this.desiredRayGroup,
      this.successfulRayGroup,
      this.projectionRayGroup,
      this.projectionEmitterGroup,
      this.blockedRayGroup,
      this.successfulIntersectionGroup,
      this.projectionIntersectionGroup,
      this.blockedIntersectionGroup,
    );

    const keyLight = new THREE.DirectionalLight(0xd8fff1, 2.6);
    keyLight.position.set(0.4, 0.8, 0.6);
    this.scene.add(keyLight, new THREE.AmbientLight(0x8db4a8, 1.2));

    const resizeObserver = new ResizeObserver(() => this.resize());
    resizeObserver.observe(host);
    this.resize();
    this.animate();
  }

  update(snapshot: SimulationSnapshot): void {
    const floorY = -snapshot.config.cubeSize.y / 2;
    this.apparatusTargetY = (snapshot.config.cubeSize.y / 2 + floorY - snapshot.config.hemisphereRadius) / 2;
    this.apparatusCameraDistance = Math.max(
      0.8,
      snapshot.config.hemisphereRadius * 2.65,
      (snapshot.config.cubeSize.y + snapshot.config.hemisphereRadius) * 1.65,
    );
    this.buildCube(snapshot);
    this.buildMeshes(snapshot);
    this.buildHemisphere(snapshot);
    this.buildEmitters(snapshot);
    this.buildRays(snapshot);
    this.applyVisibility();
  }

  updateProjectionFrame(rays: readonly RayResult[], meanSeparation: number | null = null): void {
    this.buildProjectionLayers(rays, meanSeparation);
    this.applyVisibility();
  }

  setMode(mode: ViewMode): void {
    const cameraLayoutChanged = (this.mode === 'TARGET') !== (mode === 'TARGET');
    this.mode = mode;
    if (cameraLayoutChanged) this.resetCamera();
    this.applyVisibility();
  }

  setVisibility(visibility: VisibilityState): void {
    const projectionLayoutChanged = this.visibility.projectionRays !== visibility.projectionRays;
    this.visibility = { ...visibility };
    if (this.mode === 'TARGET' && projectionLayoutChanged) this.resetCamera();
    this.applyVisibility();
  }

  resetCamera(): void {
    if (this.mode === 'TARGET' && !this.visibility.projectionRays) {
      this.camera.position.set(0, 0.03, 0.52);
      this.controls.target.set(0, 0, 0);
    } else {
      this.camera.position.set(0, this.apparatusTargetY + 0.06, this.apparatusCameraDistance);
      this.controls.target.set(0, this.apparatusTargetY, 0);
    }
    this.controls.update();
  }

  getWebGlVersion(): string {
    return this.isWebGl2 ? 'WebGL 2' : 'WebGL unavailable';
  }

  private buildCube(snapshot: SimulationSnapshot): void {
    clearGroup(this.cubeGroup);
    const { cubeSize } = snapshot.config;
    const box = new THREE.BoxGeometry(cubeSize.x, cubeSize.y, cubeSize.z);
    const shell = new THREE.Mesh(
      box,
      new THREE.MeshPhysicalMaterial({
        color: 0x6c928a,
        transparent: true,
        opacity: 0.045,
        roughness: 0.18,
        metalness: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(box),
      new THREE.LineBasicMaterial({ color: COLORS.cube, transparent: true, opacity: 0.62 }),
    );
    const floorSize = Math.max(cubeSize.x * 1.55, snapshot.config.hemisphereRadius * 2.2);
    const grid = new THREE.GridHelper(floorSize, 18, 0x35584f, 0x17332d);
    grid.position.y = -cubeSize.y / 2;
    this.cubeGroup.add(shell, edges, grid);
  }

  private buildMeshes(snapshot: SimulationSnapshot): void {
    clearGroup(this.meshGroup);
    const positions: number[] = [];
    const colors: number[] = [];
    const total = Math.max(1, snapshot.meshes.length - 1);
    for (const mesh of snapshot.meshes) {
      const tint = COLORS.mesh.clone().lerp(new THREE.Color('#9f8d68'), mesh.id / total);
      for (const segment of mesh.segments) {
        positions.push(
          segment.start.x,
          segment.start.y,
          segment.start.z,
          segment.end.x,
          segment.end.y,
          segment.end.z,
        );
        colors.push(tint.r, tint.g, tint.b, tint.r, tint.g, tint.b);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const lines = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.meshGroup.add(lines);
  }

  private buildHemisphere(snapshot: SimulationSnapshot): void {
    clearGroup(this.hemisphereGroup);
    const radius = snapshot.config.hemisphereRadius;
    const rimY = -snapshot.config.cubeSize.y / 2;
    const positions: number[] = [];
    const steps = 64;
    for (let ring = 1; ring <= 5; ring += 1) {
      const theta = (ring / 5) * (Math.PI / 2);
      for (let step = 0; step < steps; step += 1) {
        const first = (step / steps) * Math.PI * 2;
        const second = ((step + 1) / steps) * Math.PI * 2;
        const y = rimY - Math.cos(theta) * radius;
        const radial = Math.sin(theta) * radius;
        positions.push(
          Math.cos(first) * radial,
          y,
          Math.sin(first) * radial,
          Math.cos(second) * radial,
          y,
          Math.sin(second) * radial,
        );
      }
    }
    for (let meridian = 0; meridian < 12; meridian += 1) {
      const azimuth = (meridian / 12) * Math.PI * 2;
      for (let step = 0; step < 20; step += 1) {
        const first = (step / 20) * (Math.PI / 2);
        const second = ((step + 1) / 20) * (Math.PI / 2);
        positions.push(
          Math.cos(azimuth) * Math.sin(first) * radius,
          rimY - Math.cos(first) * radius,
          Math.sin(azimuth) * Math.sin(first) * radius,
          Math.cos(azimuth) * Math.sin(second) * radius,
          rimY - Math.cos(second) * radius,
          Math.sin(azimuth) * Math.sin(second) * radius,
        );
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const protectiveShell = new THREE.Mesh(
      new THREE.SphereGeometry(
        radius * 1.006,
        64,
        24,
        0,
        Math.PI * 2,
        Math.PI / 2,
        Math.PI / 2,
      ),
      new THREE.MeshPhysicalMaterial({
        color: 0x142d27,
        transparent: true,
        opacity: 0.3,
        roughness: 0.64,
        metalness: 0.18,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    protectiveShell.position.y = rimY;
    this.hemisphereGroup.add(
      protectiveShell,
      new THREE.LineSegments(
        geometry,
        new THREE.LineBasicMaterial({ color: 0x6d998c, transparent: true, opacity: 0.28 }),
      ),
    );
  }

  private buildEmitters(snapshot: SimulationSnapshot): void {
    clearGroup(this.emitterGroup);
    const geometry = new THREE.IcosahedronGeometry(0.0022, 0);
    const material = new THREE.MeshBasicMaterial({
      color: COLORS.emitter,
      transparent: true,
      opacity: 0.82,
    });
    const emitters = new THREE.InstancedMesh(geometry, material, snapshot.emitters.length);
    const matrix = new THREE.Matrix4();
    snapshot.emitters.forEach((emitter, index) => {
      matrix.makeTranslation(emitter.position.x, emitter.position.y, emitter.position.z);
      emitters.setMatrixAt(index, matrix);
    });
    emitters.instanceMatrix.needsUpdate = true;
    this.emitterGroup.add(emitters);
  }

  private buildRays(snapshot: SimulationSnapshot): void {
    clearGroup(this.desiredRayGroup);
    clearGroup(this.successfulRayGroup);
    clearGroup(this.projectionRayGroup);
    clearGroup(this.projectionEmitterGroup);
    clearGroup(this.blockedRayGroup);
    clearGroup(this.successfulIntersectionGroup);
    clearGroup(this.projectionIntersectionGroup);
    clearGroup(this.blockedIntersectionGroup);
    const desired: number[] = [];
    const successful: number[] = [];
    const projectionSuccessful: number[] = [];
    const blocked: number[] = [];
    const successPoints: number[] = [];
    const blockedPoints: number[] = [];
    const stride = Math.max(1, Math.floor(snapshot.rays.length / snapshot.config.renderedRayLimit));

    snapshot.rays.forEach((ray, index) => {
      const impact = ray.firstIntersection?.position;
      if (ray.targetAccessible && impact) {
        successPoints.push(impact.x, impact.y, impact.z);
        projectionSuccessful.push(
          ray.origin.x,
          ray.origin.y,
          ray.origin.z,
          impact.x,
          impact.y,
          impact.z,
        );
      } else if (ray.blocked && impact) blockedPoints.push(impact.x, impact.y, impact.z);

      if (index % stride !== 0 || index / stride >= snapshot.config.renderedRayLimit) return;
      desired.push(ray.origin.x, ray.origin.y, ray.origin.z, ray.target.x, ray.target.y, ray.target.z);
      const lineImpact = impact ?? ray.target;
      const destination = ray.targetAccessible ? successful : blocked;
      destination.push(ray.origin.x, ray.origin.y, ray.origin.z, lineImpact.x, lineImpact.y, lineImpact.z);
    });

    this.desiredRayGroup.add(this.lineSegments(desired, COLORS.desired, 0.18));
    this.successfulRayGroup.add(this.lineSegments(successful, COLORS.success, 0.54));
    this.projectionRayGroup.add(this.lineSegments(projectionSuccessful, COLORS.success, 0.28));
    this.blockedRayGroup.add(this.lineSegments(blocked, COLORS.blocked, 0.78));
    this.successfulIntersectionGroup.add(
      this.points(successPoints, COLORS.success, 0.014, 0.24),
      this.points(successPoints, COLORS.impact, 0.006, 0.98),
    );
    this.projectionIntersectionGroup.add(
      this.points(successPoints, COLORS.success, 0.014, 0.24),
      this.points(successPoints, COLORS.impact, 0.006, 0.98),
    );
    this.blockedIntersectionGroup.add(
      this.points(blockedPoints, COLORS.blocked, 0.007, 0.72),
    );
    this.buildProjectionEmitterPoints(snapshot.rays);
  }

  private buildProjectionLayers(rays: readonly RayResult[], meanSeparation: number | null): void {
    clearGroup(this.projectionRayGroup);
    clearGroup(this.projectionIntersectionGroup);
    clearGroup(this.projectionEmitterGroup);
    const rayPositions: number[] = [];
    const hitPositions: number[] = [];
    for (const ray of rays) {
      const impact = ray.firstIntersection?.position;
      if (!ray.targetAccessible || !impact) continue;
      rayPositions.push(
        ray.origin.x,
        ray.origin.y,
        ray.origin.z,
        impact.x,
        impact.y,
        impact.z,
      );
      hitPositions.push(impact.x, impact.y, impact.z);
    }
    this.projectionMeanSeparation = meanSeparation;
    const pointSizes = projectionPointSizes(
      meanSeparation,
      this.camera.position.distanceTo(this.controls.target),
    );
    this.projectionRayGroup.add(this.lineSegments(rayPositions, COLORS.success, 0.28));
    this.projectionIntersectionGroup.add(
      this.points(hitPositions, COLORS.success, pointSizes.halo, 0.24),
      this.points(hitPositions, COLORS.impact, pointSizes.core, 0.98),
    );
    this.buildProjectionEmitterPoints(rays);
  }

  private buildProjectionEmitterPoints(rays: readonly RayResult[]): void {
    const origins = rays
      .filter((ray) => ray.targetAccessible)
      .flatMap((ray) => [ray.origin.x, ray.origin.y, ray.origin.z]);
    this.projectionEmitterGroup.add(
      this.points(origins, COLORS.success, 0.018, 0.32),
      this.points(origins, COLORS.impact, 0.006, 0.96),
    );
  }

  private lineSegments(positions: number[], color: THREE.Color, opacity: number): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    return new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
  }

  private points(positions: number[], color: THREE.Color, size: number, opacity: number): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    return new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        color,
        size,
        map: this.pointTexture,
        sizeAttenuation: true,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
  }

  private applyVisibility(): void {
    const layers = resolveLayerVisibility(this.mode, this.visibility);
    this.cubeGroup.visible = layers.cube;
    this.meshGroup.visible = layers.meshes;
    this.hemisphereGroup.visible = layers.hemisphere;
    this.emitterGroup.visible = layers.emitters;
    this.desiredRayGroup.visible = layers.desiredRays;
    this.successfulRayGroup.visible = layers.successfulRays;
    this.projectionRayGroup.visible = layers.projectionRays;
    this.projectionEmitterGroup.visible = layers.projectionEmitters;
    this.blockedRayGroup.visible = layers.blockedRays;
    this.successfulIntersectionGroup.visible = layers.successfulIntersections;
    this.projectionIntersectionGroup.visible = layers.projectionIntersections;
    this.blockedIntersectionGroup.visible = layers.blockedIntersections;
  }

  private resize(): void {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private animate = (): void => {
    requestAnimationFrame(this.animate);
    this.controls.update();
    const pointSizes = projectionPointSizes(
      this.projectionMeanSeparation,
      this.camera.position.distanceTo(this.controls.target),
    );
    const [haloPoints, corePoints] = this.projectionIntersectionGroup.children;
    if (haloPoints instanceof THREE.Points && haloPoints.material instanceof THREE.PointsMaterial) {
      haloPoints.material.size = pointSizes.halo;
    }
    if (corePoints instanceof THREE.Points && corePoints.material instanceof THREE.PointsMaterial) {
      corePoints.material.size = pointSizes.core;
    }
    this.renderer.render(this.scene, this.camera);
    const now = performance.now();
    const elapsed = now - this.lastFrameAt;
    this.lastFrameAt = now;
    if (elapsed > 0) this.frameSamples.push(1000 / elapsed);
    if (this.frameSamples.length >= 24) {
      this.onFps(this.frameSamples.reduce((sum, value) => sum + value, 0) / this.frameSamples.length);
      this.frameSamples = [];
    }
  };
}
